import {
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Result,
  Segmented,
  Space,
  Table,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { adminApi } from "@/api/client";
import { MonospaceCell } from "@/components/MonospaceCell";

const Filters = z.object({
  email: z.string().optional(),
  user_id: z.string().optional(),
  tag_keys: z.string().optional(),
  tag_logic: z.enum(["and", "or"]),
});

type FiltersIn = z.input<typeof Filters>;
type FiltersOut = z.output<typeof Filters>;

const PRESETS_STORAGE_KEY = "admin-web-users-filter-presets-v1";

type SavedPreset = { name: string; qs: string };

function loadPresets(): SavedPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as unknown;
    if (!Array.isArray(p)) return [];
    return p.filter(
      (x): x is SavedPreset =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as SavedPreset).name === "string" &&
        typeof (x as SavedPreset).qs === "string"
    );
  } catch {
    return [];
  }
}

function savePresets(list: SavedPreset[]) {
  localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(list));
}

function parseQs(sp: URLSearchParams): Partial<FiltersOut> {
  const tagLogic = sp.get("tag_logic") === "or" ? "or" : "and";
  return {
    email: sp.get("email") ?? "",
    user_id: sp.get("user_id") ?? "",
    tag_keys: sp.get("tag_keys") ?? "",
    tag_logic: tagLogic,
  };
}

function readRangeFromQs(sp: URLSearchParams): [Dayjs, Dayjs] | null {
  const from = sp.get("created_at_from");
  const to = sp.get("created_at_to");
  if (!from || !to) return null;
  const a = Number(from);
  const b = Number(to);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return [dayjs.unix(a), dayjs.unix(b)];
}

export function UsersListPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [presets, setPresets] = useState<SavedPreset[]>(() => loadPresets());
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [presetNameDraft, setPresetNameDraft] = useState("");

  const { control, handleSubmit, reset } = useForm<FiltersIn>({
    resolver: zodResolver(Filters),
    defaultValues: parseQs(searchParams),
  });

  useEffect(() => {
    reset(parseQs(searchParams));
    setRange(readRangeFromQs(searchParams));
  }, [searchParams, reset]);

  const page = Number(searchParams.get("page") ?? "1") || 1;
  const perPage = Number(searchParams.get("per_page") ?? "20") || 20;

  const list = useQuery({
    queryKey: ["admin", "users", searchParams.toString()],
    queryFn: async () => {
      const qp: Record<string, string | number | undefined> = {
        page,
        per_page: perPage,
      };
      const email = searchParams.get("email")?.trim();
      const user_id = searchParams.get("user_id")?.trim();
      const tag_keys = searchParams.get("tag_keys")?.trim();
      const tag_logic =
        searchParams.get("tag_logic") === "or" ? "or" : "and";
      const caf = searchParams.get("created_at_from")?.trim();
      const cat = searchParams.get("created_at_to")?.trim();

      if (email) qp.email = email;
      if (user_id) qp.user_id = user_id;
      if (tag_keys) qp.tag_keys = tag_keys;
      qp.tag_logic = tag_logic;
      if (caf && cat) {
        qp.created_at_from = Number(caf);
        qp.created_at_to = Number(cat);
      }

      const { data, response } = await adminApi.GET("/admin/v1/users", {
        params: { query: qp as never },
      });
      if (!data) throw new Error(String(response.status));
      return data;
    },
  });

  const applySearchParams = useCallback(
    (q: URLSearchParams) => {
      q.set("page", "1");
      q.set("per_page", String(perPage));
      setSearchParams(q, { replace: true });
    },
    [perPage, setSearchParams]
  );

  const onFilter = handleSubmit((vals) => {
    const out = Filters.safeParse(vals);
    if (!out.success) return;
    const q = new URLSearchParams();
    if (out.data.email?.trim()) q.set("email", out.data.email.trim());
    if (out.data.user_id?.trim()) q.set("user_id", out.data.user_id.trim());
    if (out.data.tag_keys?.trim()) q.set("tag_keys", out.data.tag_keys.trim());
    q.set("tag_logic", out.data.tag_logic);

    if (range?.[0] && range[1]) {
      const from = range[0].startOf("day").unix();
      const to = range[1].endOf("day").unix();
      q.set("created_at_from", String(from));
      q.set("created_at_to", String(to));
    }

    applySearchParams(q);
  });

  const clearRange = () => {
    setRange(null);
    const next = new URLSearchParams(searchParams);
    next.delete("created_at_from");
    next.delete("created_at_to");
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const commitSavePreset = () => {
    const trimmed = presetNameDraft.trim();
    if (!trimmed) return;
    const q = new URLSearchParams(searchParams);
    q.delete("page");
    const qs = q.toString();
    const next = [...presets.filter((p) => p.name !== trimmed), { name: trimmed, qs }];
    savePresets(next);
    setPresets(next);
    setPresetModalOpen(false);
    setPresetNameDraft("");
  };

  const applyPreset = useCallback(
    (qs: string) => {
      const q = new URLSearchParams(qs);
      q.set("page", "1");
      q.set("per_page", String(perPage));
      setSearchParams(q, { replace: true });
    },
    [perPage, setSearchParams]
  );

  type Row = Record<string, unknown>;
  const columns: TableColumnsType<Row> = [
    {
      title: t("users.userId"),
      dataIndex: "id",
      render: (_, row) =>
        typeof row.id === "string" ? (
          <Link to={`/users/${row.id}`}>
            <MonospaceCell>{row.id}</MonospaceCell>
          </Link>
        ) : (
          "—"
        ),
      width: 320,
      fixed: "left",
    },
    {
      title: t("users.email"),
      dataIndex: "email",
      width: 240,
      ellipsis: true,
    },
    {
      title: t("users.createdAt"),
      dataIndex: "created_at",
      width: 160,
      render: (v: unknown) =>
        typeof v === "number" ? (
          <MonospaceCell>{dayjs.unix(v).format("YYYY-MM-DD HH:mm")}</MonospaceCell>
        ) : (
          "—"
        ),
    },
  ];

  const items = ((list.data?.items as Row[]) ?? []) as Row[];
  const useVirtual = items.length > 200;
  const scrollY = useVirtual ? 520 : undefined;

  const presetMenuItems = useMemo(
    () =>
      presets.map((p) => ({
        key: p.qs,
        label: p.name,
        onClick: () => applyPreset(p.qs),
      })),
    [presets, applyPreset]
  );

  return (
    <div style={{ width: "100%" }}>
      <h2 style={{ marginTop: 0 }}>{t("users.title")}</h2>
      <Form
        layout="inline"
        style={{ marginBottom: 16, gap: 8, flexWrap: "wrap", rowGap: 12 }}
        onFinish={() => void onFilter()}
      >
        <Form.Item label={t("users.email")}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => <Input {...field} allowClear />}
          />
        </Form.Item>
        <Form.Item label={t("users.userId")}>
          <Controller
            name="user_id"
            control={control}
            render={({ field }) => <Input {...field} allowClear />}
          />
        </Form.Item>
        <Form.Item label={t("users.tags")}>
          <Controller
            name="tag_keys"
            control={control}
            render={({ field }) => (
              <Input {...field} allowClear placeholder="tag_a,tag_b" />
            )}
          />
        </Form.Item>
        <Form.Item label={t("users.tagLogic")}>
          <Controller
            name="tag_logic"
            control={control}
            render={({ field }) => (
              <Segmented
                {...field}
                options={[
                  { label: t("users.tagLogicAnd"), value: "and" },
                  { label: t("users.tagLogicOr"), value: "or" },
                ]}
              />
            )}
          />
        </Form.Item>
        <Form.Item label={t("users.createdRange")}>
          <DatePicker.RangePicker
            value={range as [Dayjs, Dayjs] | null}
            onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
          />
        </Form.Item>
        <Form.Item>
          <Space wrap>
            <Button type="primary" htmlType="submit">
              {t("common.submit")}
            </Button>
            {range?.[0] && range[1] ? (
              <Button onClick={clearRange}>{t("users.clearDateRange")}</Button>
            ) : null}
            <Dropdown menu={{ items: presetMenuItems }} disabled={!presetMenuItems.length}>
              <Button>{t("users.loadPreset")}</Button>
            </Dropdown>
            <Button
              onClick={() => {
                setPresetNameDraft("");
                setPresetModalOpen(true);
              }}
            >
              {t("users.savePreset")}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {useVirtual ? (
        <div style={{ marginBottom: 8, fontSize: 12, color: "var(--ant-color-text-secondary)" }}>
          {t("users.virtualHint", { n: 200 })}
        </div>
      ) : null}

      {list.error ? (
        <Result
          status="error"
          title={t("users.error")}
          extra={
            <Button type="primary" onClick={() => list.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      ) : (
        <Table<Row>
          virtual={useVirtual}
          loading={list.isPending}
          rowKey={(r, idx) =>
            typeof r.id === "string" && r.id
              ? r.id
              : `users-${page}-${idx ?? 0}`
          }
          dataSource={items}
          scroll={{ x: 900, y: scrollY }}
          columns={columns}
          locale={{
            emptyText: (
              <Empty description={t("users.empty")}>
                <Button type="link" onClick={() => list.refetch()}>
                  {t("common.retry")}
                </Button>
              </Empty>
            ),
          }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: list.data?.total,
            pageSizeOptions: [20, 50, 100, 200, 500],
            showSizeChanger: true,
            position: ["bottomCenter"],
            onChange: (p, ps) => {
              const next = new URLSearchParams(searchParams);
              next.set("page", String(p));
              next.set("per_page", String(ps));
              setSearchParams(next, { replace: true });
            },
          }}
        />
      )}

      <Modal
        title={t("users.savePresetTitle")}
        open={presetModalOpen}
        onOk={commitSavePreset}
        onCancel={() => {
          setPresetModalOpen(false);
          setPresetNameDraft("");
        }}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
      >
        <Input
          placeholder={t("users.savePresetPlaceholder")}
          value={presetNameDraft}
          onChange={(e) => setPresetNameDraft(e.target.value)}
        />
      </Modal>
    </div>
  );
}
